import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import appService from '../../service/app.service';

const AutoCompleteSelectUI = (props) => {
  const {
    id,
    label,
    table,
    listType,
    selectedValue,
    selectedLabelValue,
    conditions,
    joins,
    multiple,
    selectedColumns,
    dataset,
    variant,
    isOptionEqualToValue,
  } = props;

  const [list, setList] = useState([]);
  const [defaultList, setDefaultList] = useState([]);
  const [value, setValue] = useState(multiple ? [] : null);

  useEffect(() => {
    fetchData();
  }, [table, conditions]);

  useEffect(() => {
    setList(defaultList);
  }, [defaultList]);

  useEffect(() => {
    if (dataset) setDefaultList(dataset);
  }, [dataset]);

  useEffect(() => {
    const findedListItem = list.find((p) => p.value === selectedValue);

    if (
      !findedListItem &&
      ((typeof selectedValue === 'number' && selectedValue > 0) ||
        (typeof selectedValue === 'string' && selectedValue.trim().length > 0))
    ) {
      const newVal = { value: selectedValue, label: selectedLabelValue ?? '' };
      setList([...defaultList, newVal]);
      setValue(newVal);
    } else {
      if (multiple) {
        setList(defaultList);
        setValue(selectedValue);
      } else {
        setList(defaultList);
        setValue(findedListItem || null);
      }
    }
  }, [selectedValue, selectedLabelValue, defaultList]);

  const fetchData = () => {
    if (table) {
      const requestData = {
        tables: Array.isArray(table) ? table : [table],
        joins: Array.isArray(joins) ? joins : joins ? [joins] : [],
        conditions: Array.isArray(conditions) ? conditions : conditions ? [conditions] : [],
        selectedColumns: Array.isArray(selectedColumns) ? selectedColumns : selectedColumns ? [selectedColumns] : ['*'],
      };

      appService.getJoinedData(requestData).then((data) => {
        const formattedData = data.map((item) => ({
          label: item[listType.name],
          value: item[listType.id],
          ...(listType?.type ? { type: item[listType.type] } : item.type ? { type: item.type } : {}),
          ...(item.icon ? { icon: item.icon } : {}),
        }));
        setDefaultList(formattedData);
      });
    } else {
      setDefaultList([]);
    }
  };

  const filterOptions = createFilterOptions({
    limit: 100,
  });

  const handleKeyDown = (event) => {
    if (event.key === 'Tab') {
      const inputValue = event.target?.value?.trim();
      if (inputValue) {
        const exactOption = list.find(
          (opt) => String(opt.label).trim() === inputValue || String(opt.value).trim() === inputValue
        );

        if (exactOption) {
          if (multiple) {
            const current = Array.isArray(value) ? value : [];
            const exists = current.some((opt) => opt.value === exactOption.value);
            const newValue = exists ? current : [...current, exactOption];
            setValue(exactOption);
            if (props.onChange) {
              props.onChange(event, newValue);
            }
          } else {
            setValue(exactOption);
            if (props.onChange) {
              props.onChange(event, exactOption);
            }
          }
        }
      }
    }

    if (props.onKeyDown) {
      props.onKeyDown(event);
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (props.onChange) {
      props.onChange(event, newValue);
    }
  };

  return (
    <div className="z-50 w-full">
      <Autocomplete
        clearOnEscape
        id={id}
        name={id}
        value={value}
        options={list}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        isOptionEqualToValue={isOptionEqualToValue ?? ((option, val) => option?.value === val?.value)}
        sx={{ width: '100%' }}
        slotProps={{
          popper: {
            placement: 'bottom-start',
            sx: {
              width: 'auto !important',
              maxWidth: '100vw', // do not exceed viewport width
              '& .MuiAutocomplete-paper': {
                width: 'max-content',
                minWidth: 150,
                maxWidth: 'inherit', // respect viewport limit
                whiteSpace: 'normal', // wrap long labels
              },
            },
          },
        }}
        filterOptions={filterOptions}
        renderInput={(params) => {
          const selectedIcon = !multiple && value?.icon ? value.icon : null;
          return (
            <TextField
              {...params}
              variant={variant ?? 'standard'}
              label={label}
              helperText={props.helperText}
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedIcon ? (
                  <InputAdornment position="start">{selectedIcon}</InputAdornment>
                ) : (
                  params.InputProps.startAdornment
                ),
              }}
            />
          );
        }}
        renderOption={(props, option) => {
          return (
            <li {...props} key={uuidv4()}>
              {option.icon ? <span style={{ display: 'inline-flex', marginRight: 8 }}>{option.icon}</span> : null}
              {option.label}
            </li>
          );
        }}
        {...props}
      />
    </div>
  );
};
export default AutoCompleteSelectUI;
